import { useUser, useBusiness } from '@/api';
import { Greeting } from '@/components/atoms';
import { AppSidebar, LayoutHeader } from '@/components/molecules';
import {
  PlanGateProvider,
  ProductTourProvider,
  SidebarProvider,
} from '@/components/organisms';
import { routes } from '@/lib/constants';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { useEffect } from 'react';

export const DashboardLayout = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isHome = pathname === routes.root;
  const {
    data: user,
    isError: isUserError,
    error: userError,
    isLoading: isUserLoading,
  } = useUser('me');
  const { data: business, isLoading: isBusinessLoading } = useBusiness();

  useEffect(() => {
    if (isUserLoading || isBusinessLoading) return;

    // Incomplete onboarding — send to business profile, not dashboard.
    if (user && !business && !user.business) {
      navigate(routes.signUp, {
        replace: true,
        state: { step: 3, userId: user.id },
      });
      return;
    }

    // Safety net: if auth check fails, force redirect to login.
    if (!isUserError) return;
    const msg =
      (userError as Error | undefined)?.message?.toLowerCase?.() ?? '';
    const shouldRedirect =
      msg.includes('unauthorized') ||
      msg.includes('401') ||
      msg.includes('login') ||
      msg.includes('session');

    if (shouldRedirect || msg.length > 0) {
      navigate(routes.login, { replace: true });
    } else {
      navigate(routes.login, { replace: true });
    }
  }, [
    business,
    isBusinessLoading,
    isUserError,
    isUserLoading,
    navigate,
    user,
    userError,
  ]);

  return (
    <ProductTourProvider>
      <PlanGateProvider>
        <SidebarProvider>
          <AppSidebar />
          <div className="p-4 w-full flex flex-col gap-4 md:gap-6 lg:gap-10 relative overflow-hidden">
            <LayoutHeader />
            <div className="mt-18">
              {isHome && user?.firstName && <Greeting name={user?.firstName} />}
              <Outlet />
            </div>
          </div>
        </SidebarProvider>
      </PlanGateProvider>
    </ProductTourProvider>
  );
};
